import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
}

export interface LoginData {
  username: string;  // Email is used as username
  password: string;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface UserResponse {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: string;
  created_at: string;
  loyalty_points_balance: number;
}

export interface UserUpdateData {
  first_name?: string;
  last_name?: string;
  phone?: string;
}

export const authAPI = {
  // Register new user
  register: async (data: RegisterData): Promise<UserResponse> => {
    const response = await axios.post(`${API_BASE}/auth/register`, data);
    return response.data;
  },

  // Login user
  login: async (email: string, password: string): Promise<AuthTokens> => {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const response = await axios.post(
      `${API_BASE}/auth/login`,
      formData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );

    // Store tokens in localStorage
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }

    return response.data;
  },

  // Logout user
  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },

  // Exchange the stored refresh token for a new access token. The refresh
  // token itself is left in place (the backend does not rotate it). Throws if
  // there is no refresh token or the backend rejects it; callers decide
  // whether to log out.
  refreshAccessToken: async (): Promise<string> => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    const response = await axios.post(`${API_BASE}/auth/refresh`, {
      refresh_token: refreshToken,
    });
    localStorage.setItem('access_token', response.data.access_token);
    return response.data.access_token;
  },

  // Get stored access token
  getAccessToken: (): string | null => {
    return localStorage.getItem('access_token');
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('access_token');
    return !!token;
  },

  // Get current user info
  getCurrentUser: async (): Promise<UserResponse> => {
    const response = await axios.get(`${API_BASE}/auth/me`);
    return response.data;
  },

  // Update current user info
  updateCurrentUser: async (data: UserUpdateData): Promise<UserResponse> => {
    const response = await axios.put(`${API_BASE}/auth/me`, data);
    return response.data;
  },
};

// Add axios interceptor to include token in requests
axios.interceptors.request.use(
  (config) => {
    const token = authAPI.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);